/**
 * SKI AXIS · /chapters — Chapter Lead programme info page.
 *
 * Public-facing info page describing the FI-inspired distributed expansion
 * model. Three-tier structure, territory map, what you get / what we get,
 * FAQ, and a primary CTA → /chapters/apply.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Globe2, ArrowRight, MapPin, Award, Users, Sparkles, Shield, TrendingUp,
  Compass, Megaphone, FileText, GraduationCap, Calculator, Target, BarChart3,
  ClipboardCheck, Handshake, Coins,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const STYLES = `
.cl-root{background:#FAF7F0;color:#0D1B2A;min-height:100vh;font-family:'DM Sans',-apple-system,sans-serif;}
.cl-shell{max-width:1200px;margin:0 auto;padding:64px 32px;}
@media(max-width:680px){.cl-shell{padding:40px 22px;}}

/* HERO */
.cl-hero{padding:64px 0 48px;border-bottom:1px solid rgba(13,27,42,.08);}
.cl-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;display:flex;align-items:center;gap:14px;margin-bottom:22px;}
.cl-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
.cl-h1{font-family:'Cormorant Display',serif;font-size:clamp(46px,6.5vw,80px);font-weight:700;line-height:1.0;letter-spacing:-.025em;margin:0 0 22px;color:#0D1B2A;max-width:1100px;}
.cl-h1 em{font-style:italic;color:#C8A84B;font-weight:600;}
.cl-sub{font-size:19px;line-height:1.5;color:#3D4A5C;max-width:780px;font-weight:300;margin:0 0 28px;}
.cl-cta-row{display:flex;flex-wrap:wrap;gap:14px;margin-top:8px;}
.cl-cta{display:inline-flex;align-items:center;gap:10px;background:#0D1B2A;color:#C8A84B;padding:18px 32px;font-size:12px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;border:1px solid #0D1B2A;transition:all .2s;}
.cl-cta:hover{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.cl-cta-2{display:inline-flex;align-items:center;gap:10px;color:#0D1B2A;padding:18px 28px;font-size:12px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;text-decoration:none;border:1px solid rgba(13,27,42,.25);transition:all .2s;}
.cl-cta-2:hover{border-color:#C8A84B;color:#C8A84B;}

/* WHAT IS */
.cl-what{margin-top:80px;background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;padding:64px 56px;border-left:4px solid #C8A84B;position:relative;overflow:hidden;}
.cl-what::before{content:'';position:absolute;top:-60px;right:-60px;width:280px;height:280px;background:radial-gradient(circle,rgba(200,168,75,.15) 0%,transparent 70%);}
.cl-what-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;margin-bottom:16px;display:flex;align-items:center;gap:14px;}
.cl-what-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
.cl-what-h{font-family:'Cormorant Display',serif;font-size:clamp(30px,4vw,46px);font-weight:700;line-height:1.1;letter-spacing:-.014em;margin:0 0 18px;color:#FAF7F0;}
.cl-what-h em{font-style:italic;color:#C8A84B;}
.cl-what-p{font-size:16px;line-height:1.7;color:rgba(250,247,240,.8);font-weight:300;margin:0 0 14px;max-width:820px;}

/* THREE-TIER */
.cl-tier{margin-top:96px;}
.cl-h2{font-family:'Cormorant Display',serif;font-size:clamp(36px,4.5vw,52px);font-weight:700;line-height:1.05;letter-spacing:-.018em;margin:0 0 14px;color:#0D1B2A;}
.cl-h2 em{font-style:italic;color:#C8A84B;}
.cl-h2-sub{font-size:15.5px;color:#3D4A5C;max-width:680px;margin:0 0 36px;line-height:1.55;}
.cl-tier-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px;align-items:stretch;}
@media(max-width:880px){.cl-tier-grid{grid-template-columns:1fr;}}
.cl-tier-card{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:32px 30px;display:flex;flex-direction:column;}
.cl-tier-card.feat{background:linear-gradient(180deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;border:1px solid #C8A84B;}
.cl-tier-num{font-family:'Cormorant Display',serif;font-style:italic;font-size:14px;font-weight:600;letter-spacing:.16em;text-transform:uppercase;color:#C8A84B;margin-bottom:14px;}
.cl-tier-name{font-family:'Cormorant Display',serif;font-size:24px;font-weight:700;color:#0D1B2A;line-height:1.2;letter-spacing:-.008em;margin:0 0 10px;}
.cl-tier-card.feat .cl-tier-name{color:#FAF7F0;}
.cl-tier-card.feat .cl-tier-name em{color:#C8A84B;font-style:italic;}
.cl-tier-d{font-size:13.5px;color:#3D4A5C;line-height:1.6;margin:0 0 16px;flex:1;}
.cl-tier-card.feat .cl-tier-d{color:rgba(250,247,240,.75);}
.cl-tier-tag{font-size:10px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#C8A84B;border-top:1px solid rgba(200,168,75,.2);padding-top:14px;}

/* TERRITORIES */
.cl-terr{margin-top:96px;}
.cl-terr-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-top:24px;}
@media(max-width:880px){.cl-terr-grid{grid-template-columns:repeat(2,1fr);}}
@media(max-width:480px){.cl-terr-grid{grid-template-columns:1fr;}}
.cl-terr-card{background:#fff;border:1px solid #E8DFC9;padding:18px 20px;display:flex;align-items:center;gap:12px;transition:all .2s;}
.cl-terr-card:hover{border-color:#C8A84B;transform:translateY(-2px);box-shadow:0 8px 24px rgba(13,27,42,.04);}
.cl-terr-card.hq{background:#0D1B2A;color:#FAF7F0;border-color:#C8A84B;}
.cl-terr-card.hq .cl-terr-name{color:#C8A84B;}
.cl-terr-icn{flex-shrink:0;width:36px;height:36px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:rgba(200,168,75,.1);color:#C8A84B;}
.cl-terr-card.hq .cl-terr-icn{background:rgba(200,168,75,.18);}
.cl-terr-name{font-family:'Cormorant Display',serif;font-size:18px;font-weight:700;color:#0D1B2A;letter-spacing:-.005em;line-height:1.2;}
.cl-terr-status{font-size:9.5px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#C8A84B;margin-top:3px;}
.cl-terr-card.hq .cl-terr-status{color:rgba(250,247,240,.6);}

/* WHAT YOU GET */
.cl-mutual{margin-top:96px;display:grid;grid-template-columns:1fr 1fr;gap:32px;}
@media(max-width:880px){.cl-mutual{grid-template-columns:1fr;}}
.cl-mutual-col{background:#fff;border:1px solid #E8DFC9;padding:48px 40px;}
.cl-mutual-col.dark{background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;border:1px solid #C8A84B;border-left:4px solid #C8A84B;}
.cl-mutual-eb{font-size:10.5px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:#C8A84B;margin-bottom:14px;}
.cl-mutual-h{font-family:'Cormorant Display',serif;font-size:30px;font-weight:700;line-height:1.1;color:#0D1B2A;letter-spacing:-.012em;margin:0 0 22px;}
.cl-mutual-h em{font-style:italic;color:#C8A84B;}
.cl-mutual-col.dark .cl-mutual-h{color:#FAF7F0;}
.cl-mutual-col ul{margin:0;padding:0;list-style:none;}
.cl-mutual-col li{font-size:14px;color:#0D1B2A;padding:12px 0;border-top:1px solid #E8DFC9;display:flex;align-items:flex-start;gap:11px;line-height:1.55;}
.cl-mutual-col li:first-child{border:0;}
.cl-mutual-col li svg{color:#C8A84B;flex-shrink:0;margin-top:3px;}
.cl-mutual-col.dark li{color:rgba(250,247,240,.85);border-top-color:rgba(200,168,75,.18);}
.cl-mutual-col li strong{color:#0D1B2A;font-weight:700;}
.cl-mutual-col.dark li strong{color:#C8A84B;font-weight:700;}

/* TERMS BAND */
.cl-terms{margin-top:48px;background:#0D1B2A;color:#FAF7F0;padding:48px 56px;border-top:3px solid #C8A84B;border-bottom:3px solid #C8A84B;display:grid;grid-template-columns:repeat(3,1fr);gap:36px;align-items:center;}
@media(max-width:760px){.cl-terms{grid-template-columns:1fr;padding:36px 28px;gap:28px;}}
.cl-terms-block{text-align:center;border-right:1px solid rgba(200,168,75,.18);padding-right:28px;}
@media(max-width:760px){.cl-terms-block{border-right:0;border-bottom:1px solid rgba(200,168,75,.18);padding:0 0 22px;}.cl-terms-block:last-child{border-bottom:0;padding-bottom:0;}}
.cl-terms-block:last-child{border-right:0;padding-right:0;}
.cl-terms-eb{font-size:10.5px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:#C8A84B;margin-bottom:10px;}
.cl-terms-num{font-family:'Cormorant Display',serif;font-size:64px;font-weight:700;line-height:1;color:#FAF7F0;letter-spacing:-.02em;margin:0;}
.cl-terms-num em{color:#C8A84B;font-style:italic;font-size:32px;font-weight:600;}
.cl-terms-d{font-size:12.5px;color:rgba(250,247,240,.65);margin-top:8px;font-style:italic;font-family:'Cormorant Display',serif;font-size:14.5px;line-height:1.5;}

/* FAQ */
.cl-faq{margin-top:96px;}
.cl-faq-grid{margin-top:24px;display:flex;flex-direction:column;gap:0;}
.cl-faq-item{padding:24px 0;border-top:1px solid #E8DFC9;}
.cl-faq-item:first-child{border-top:0;}
.cl-faq-q{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:#0D1B2A;line-height:1.3;letter-spacing:-.005em;margin:0 0 8px;}
.cl-faq-q em{font-style:italic;color:#C8A84B;}
.cl-faq-a{font-size:14.5px;color:#3D4A5C;line-height:1.7;margin:0;max-width:900px;}

/* CTA STRIP */
.cl-final{margin-top:96px;background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;padding:64px 56px;text-align:center;border-left:4px solid #C8A84B;position:relative;overflow:hidden;}
.cl-final::before{content:'';position:absolute;top:-80px;left:-80px;width:300px;height:300px;background:radial-gradient(circle,rgba(200,168,75,.12) 0%,transparent 70%);}
.cl-final h3{font-family:'Cormorant Display',serif;font-size:clamp(28px,4vw,42px);font-weight:700;line-height:1.1;margin:0 0 14px;color:#FAF7F0;letter-spacing:-.012em;}
.cl-final h3 em{font-style:italic;color:#C8A84B;}
.cl-final p{font-size:15px;color:rgba(250,247,240,.7);max-width:680px;margin:0 auto 28px;line-height:1.6;font-weight:300;}
.cl-final-btn{display:inline-flex;align-items:center;gap:10px;background:#C8A84B;color:#0D1B2A;padding:18px 36px;font-size:13px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;transition:all .2s;}
.cl-final-btn:hover{background:#FAF7F0;transform:translateY(-2px);}

/* PILLARS — Roles & Responsibilities */
.cl-pillars{margin-top:96px;}
.cl-pillar-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:14px;margin-top:28px;}
@media(max-width:980px){.cl-pillar-grid{grid-template-columns:repeat(2,1fr);}}
@media(max-width:560px){.cl-pillar-grid{grid-template-columns:1fr;}}
.cl-pillar{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:28px 22px;display:flex;flex-direction:column;gap:10px;transition:transform .18s,box-shadow .18s;}
.cl-pillar:hover{transform:translateY(-3px);box-shadow:0 12px 28px rgba(13,27,42,.06);}
.cl-pillar-icn{width:40px;height:40px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:rgba(200,168,75,.12);color:#C8A84B;}
.cl-pillar-num{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-size:13px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;}
.cl-pillar-name{font-family:'Cormorant Display',serif;font-size:21px;font-weight:700;color:#0D1B2A;line-height:1.2;letter-spacing:-.005em;margin:0 0 4px;}
.cl-pillar-d{font-size:13px;color:#3D4A5C;line-height:1.6;margin:0 0 10px;flex:1;}
.cl-pillar ul{margin:0;padding:0;list-style:none;}
.cl-pillar li{font-size:12px;color:#0D1B2A;padding:6px 0 6px 14px;line-height:1.5;border-top:1px solid #FAF0DC;position:relative;}
.cl-pillar li:first-child{border:0;}
.cl-pillar li::before{content:'·';color:#C8A84B;position:absolute;left:2px;top:5px;font-weight:800;}

/* COMPENSATION ARCHITECTURE */
.cl-comp{margin-top:96px;background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;padding:56px 56px;border-left:4px solid #C8A84B;position:relative;overflow:hidden;}
@media(max-width:680px){.cl-comp{padding:38px 26px;}}
.cl-comp::before{content:'';position:absolute;top:-100px;right:-80px;width:340px;height:340px;background:radial-gradient(circle,rgba(200,168,75,.1) 0%,transparent 70%);}
.cl-comp-h{font-family:'Cormorant Display',serif;font-size:clamp(30px,4vw,46px);font-weight:700;line-height:1.1;color:#FAF7F0;letter-spacing:-.014em;margin:0 0 14px;}
.cl-comp-h em{font-style:italic;color:#C8A84B;}
.cl-comp-sub{font-size:15px;color:rgba(250,247,240,.7);max-width:780px;line-height:1.6;font-weight:300;margin:0 0 32px;}
.cl-comp-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-bottom:32px;}
@media(max-width:880px){.cl-comp-grid{grid-template-columns:1fr;}}
.cl-comp-card{background:rgba(255,255,255,.04);border:1px solid rgba(200,168,75,.22);padding:24px 22px;}
.cl-comp-eb{font-size:10.5px;font-weight:700;letter-spacing:.26em;color:#C8A84B;text-transform:uppercase;margin-bottom:10px;}
.cl-comp-num{font-family:'Cormorant Display',serif;font-size:42px;font-weight:700;color:#FAF7F0;line-height:1;letter-spacing:-.02em;margin:0 0 6px;}
.cl-comp-num em{color:#C8A84B;font-style:italic;font-size:24px;font-weight:600;}
.cl-comp-tag{font-size:12px;color:rgba(250,247,240,.78);line-height:1.55;font-style:italic;font-family:'Cormorant Display',serif;font-size:14px;margin:0;}
.cl-comp-example{background:rgba(200,168,75,.08);border:1px dashed rgba(200,168,75,.4);padding:24px 28px;}
.cl-comp-example-eb{font-size:10.5px;font-weight:700;letter-spacing:.28em;color:#C8A84B;text-transform:uppercase;margin-bottom:14px;}
.cl-comp-example p{font-size:14px;line-height:1.7;color:rgba(250,247,240,.9);margin:0 0 8px;}
.cl-comp-example strong{color:#C8A84B;font-weight:700;}
.cl-comp-example .num{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-weight:700;}

/* KPI / KRA TABLE */
.cl-kpi{margin-top:96px;}
.cl-kpi-table{background:#fff;border:1px solid #E8DFC9;margin-top:24px;overflow:hidden;}
.cl-kpi-row{display:grid;grid-template-columns:1.1fr 1fr 0.7fr 0.6fr;gap:0;align-items:stretch;border-top:1px solid #E8DFC9;}
.cl-kpi-row:first-child{border-top:0;background:#0D1B2A;color:#FAF7F0;}
.cl-kpi-row:first-child .cl-kpi-cell{color:#C8A84B;font-size:10.5px;font-weight:800;letter-spacing:.22em;text-transform:uppercase;padding:16px 20px;border-right:1px solid rgba(200,168,75,.2);}
.cl-kpi-cell{padding:18px 20px;font-size:13.5px;color:#0D1B2A;line-height:1.55;border-right:1px solid #FAF0DC;display:flex;align-items:center;}
.cl-kpi-cell:last-child{border-right:0;}
.cl-kpi-cell.metric{font-weight:700;color:#0D1B2A;font-family:'Cormorant Display',serif;font-size:17px;letter-spacing:-.005em;}
.cl-kpi-cell.target{font-family:'Cormorant Display',serif;font-style:italic;font-size:18px;color:#C8A84B;font-weight:700;}
.cl-kpi-cell.cad{font-size:10.5px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:#3D4A5C;}
.cl-kpi-cell.weight{font-weight:800;color:#0D1B2A;font-size:14px;}
@media(max-width:760px){
  .cl-kpi-row{grid-template-columns:1fr;}
  .cl-kpi-row:first-child{display:none;}
  .cl-kpi-cell{border-right:0;border-top:1px dashed #FAF0DC;padding:6px 18px;}
  .cl-kpi-cell:first-child{border-top:0;padding-top:14px;font-weight:800;}
  .cl-kpi-cell:last-child{padding-bottom:14px;}
}

/* JOURNEY TIMELINE */
.cl-journey{margin-top:96px;}
.cl-journey-rail{margin-top:32px;display:grid;grid-template-columns:repeat(6,1fr);gap:8px;position:relative;}
@media(max-width:980px){.cl-journey-rail{grid-template-columns:repeat(3,1fr);}}
@media(max-width:560px){.cl-journey-rail{grid-template-columns:1fr;}}
.cl-stage{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:24px 18px;display:flex;flex-direction:column;gap:8px;position:relative;}
.cl-stage-num{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-size:30px;font-weight:700;line-height:1;letter-spacing:-.02em;}
.cl-stage-when{font-size:9.5px;font-weight:800;letter-spacing:.22em;text-transform:uppercase;color:#3D4A5C;}
.cl-stage-name{font-family:'Cormorant Display',serif;font-size:18px;font-weight:700;color:#0D1B2A;line-height:1.2;letter-spacing:-.005em;margin:2px 0 4px;}
.cl-stage-d{font-size:12px;color:#3D4A5C;line-height:1.55;margin:0;}

`;

const TERRITORIES = [
  { name: 'Dubai', status: 'HQ · Dr. Neha Dewan', hq: true },
  { name: 'London', status: 'Open' },
  { name: 'Amsterdam', status: 'Open' },
  { name: 'Mumbai', status: 'Open' },
  { name: 'Bangalore', status: 'Open' },
  { name: 'Riyadh', status: 'Open' },
  { name: 'Doha', status: 'Open' },
  { name: 'Singapore', status: 'Open' },
  { name: 'Kuala Lumpur', status: 'Open' },
  { name: 'Nairobi', status: 'Open' },
  { name: 'Lagos', status: 'Open' },
  { name: 'Other', status: 'Propose your city' },
];

const FAQ = [
  {
    q: 'Is this a full-time role?',
    a: 'No. Most Chapter Leads are existing ecosystem operators — running an angel network, a co-working space, a previous accelerator chapter, or an advisory practice. SKI Axis Chapter Lead duties typically need 10–20 hours per week during cohort recruitment cycles, less between cycles.',
  },
  {
    q: 'How is this different from being a mentor or advisor?',
    a: 'Mentors give time. Chapter Leads own a market. You\'re the named SKI Axis face in your city, you build the local community under our brand, you screen and source founders, and you have economic upside — revenue share + equity in the founders you place. It\'s a partnership, not a volunteer role.',
  },
  {
    q: 'Do I need prior accelerator experience?',
    a: 'Not strictly. We weight network depth, governance literacy, and operating credibility more than prior accelerator titles. If you have run cohorts before — Founder Institute, Endeavor, Techstars chapters, Y Combinator alumni programmes, sector communities — that helps. If you have not but you have a strong founder or investor network in your city, we want to hear from you.',
  },
  {
    q: 'How long is the commitment?',
    a: 'Initial term is 12 months, automatically renewing. Either party can terminate with 60 days\' notice. Performance is reviewed quarterly against founder-pipeline and cohort-completion metrics.',
  },
  {
    q: 'What about Founder Institute / other accelerators in my city?',
    a: 'No conflict by default — SKI Axis is a governance-first programme positioned downstream of pre-pitch accelerators. Founder Institute, for example, takes founders from idea to pitch-ready; SKI Axis takes them from pitch-ready to institutionally investor-ready. A Chapter Lead running both creates the most complete founder journey in their market.',
  },
  {
    q: 'What is the equity structure exactly?',
    a: '1.0% common-stock advisor-style allocation in each cohort company you place, vesting over 24 months from cohort completion, contingent on the company completing the full 12-week SKI Axis programme. Subject to a standard accelerator equity grant framework — full terms shared at offer stage.',
  },
  {
    q: 'When does the first cohort run?',
    a: 'Cohort 1 runs July 2026 from Dubai. Cohort 2 (October 2026) is the first cohort with international Chapter Lead-sourced founders. Apply now to be ready for Cohort 2 placement.',
  },
  {
    q: 'When and how is revenue share paid?',
    a: 'Revenue share is calculated on the gross cohort fee paid by each founder you source. It is paid in full within 30 days of cohort completion (Week 13). For founders paying in instalments, your share is paid pro-rata as instalments are received. All payments are made by SWIFT or local-rail in your jurisdiction; the platform statement is reconciled monthly inside your Chapter Lead dashboard.',
  },
  {
    q: 'Are there secondary income streams beyond the cohort fee?',
    a: 'Yes — five. (1) 15% revenue share on any Master Faculty webinar a founder from your territory pays for (within 90 days of placement). (2) 10% origination fee on any Investors Circle membership you source in your city. (3) Speaking honorarium when you stand on a Forum or Master Faculty stage. (4) Equity allocation upside (1% per founder placed). (5) Carried-interest participation on any direct SKI Axis follow-on investment into a founder you placed (capped at 0.5% of the SKI Axis carry, vesting 4 years).',
  },
  {
    q: 'What happens if I miss my quarterly KPIs?',
    a: 'One miss triggers a structured 30-day support cycle with the Executive Board: we look at sourcing channels, conversion friction, and territory-specific obstacles, then re-baseline. Two consecutive misses move you to "on hold" status (no new founder placements) until a recovery plan is signed. Three consecutive misses end the appointment with the standard 60-day notice. The framework is firm but never punitive — if the territory is genuinely difficult, we lower the targets, we don\'t lower the standards.',
  },
  {
    q: 'Do I get to keep my day job?',
    a: 'Yes — and we expect it. The strongest Chapter Leads are operators with day-job credibility (existing accelerator chair, family-office partner, ecosystem builder, founder of a related platform). The Chapter Lead role is designed to be additive to your existing operating identity — not a replacement for it.',
  },
  {
    q: 'Will the Chapter Lead agreement be in writing?',
    a: 'Yes. Once an offer is made, you receive a formal Chapter Lead Appointment Letter and Master Services Agreement covering scope, IP, brand-licensing, KPIs, compensation, equity vesting, exit terms, and dispute resolution. Both documents are reviewed by SKI Asia Pacific\'s legal counsel and yours before signing. No work begins before signing.',
  },
];

// =================== ROLES & RESPONSIBILITIES — 5 PILLARS ===================
const PILLARS = [
  {
    icn: <Compass className="h-5 w-5" />,
    num: 'Pillar I',
    name: 'Source',
    d: 'Find the founders in your corridor who deserve a seat at SKI Axis — and would never apply to anyone else.',
    items: [
      '3–8 qualified Cohort applications per cycle',
      'Pre-screen against the central scorecard',
      'Personally vouched referrals weighted highest',
    ],
  },
  {
    icn: <Megaphone className="h-5 w-5" />,
    num: 'Pillar II',
    name: 'Convene',
    d: 'Host the rooms that matter. Investors Circle, Master Faculty events, founder office hours — under the SKI Axis brand in your city.',
    items: [
      '4–6 governance / pre-accelerator workshops per year',
      'Monthly Investors Circle drinks (or async equivalent)',
      'Annual SKI Axis flagship event in your territory',
    ],
  },
  {
    icn: <Handshake className="h-5 w-5" />,
    num: 'Pillar III',
    name: 'Represent',
    d: 'Be the institutional face of SKI Axis at policy, ministry, free-zone, and family-office level — with us, not for us.',
    items: [
      'Quarterly meeting with at least one ministry / regulator',
      'Accredited speaking slot at one major local conference / year',
      'Press, podcast, and university representation rights',
    ],
  },
  {
    icn: <Shield className="h-5 w-5" />,
    num: 'Pillar IV',
    name: 'Govern',
    d: 'Apply the SKI Axis governance standard locally — consistent vetting, conflict declarations, and quality control.',
    items: [
      'Sign + uphold the Chapter Lead Code of Conduct',
      'Declare conflicts of interest before sourcing',
      'Maintain confidentiality on all founder data',
    ],
  },
  {
    icn: <FileText className="h-5 w-5" />,
    num: 'Pillar V',
    name: 'Report',
    d: 'Run a transparent operation — pipeline, conversions, events, ministry meetings — so HQ can support, scale, and compensate you correctly.',
    items: [
      'Weekly pipeline update in Chapter Lead dashboard',
      'Monthly written territory report (one page)',
      'Quarterly accountability call with Executive Board',
    ],
  },
];

// =================== KPIs / KRAs ===================
const KPIS = [
  { metric: 'Qualified Cohort applications sourced', target: '3–8 / cycle', cadence: 'Per cohort', weight: '25%' },
  { metric: 'Cohort founders successfully placed', target: '1–3 / cycle', cadence: 'Per cohort', weight: '25%' },
  { metric: 'Local workshops / events hosted', target: '4–6 / year', cadence: 'Quarterly', weight: '15%' },
  { metric: 'Investors Circle members originated', target: '6 / year', cadence: 'Quarterly', weight: '10%' },
  { metric: 'Ministry / regulator engagements', target: '4 / year', cadence: 'Quarterly', weight: '10%' },
  { metric: 'Master Faculty webinar attendance from your territory', target: '20+ / year', cadence: 'Annual', weight: '10%' },
  { metric: 'Monthly written territory report submitted', target: '12 / 12', cadence: 'Monthly', weight: '5%' },
];

// =================== JOURNEY — 6-STAGE LIFECYCLE ===================
const JOURNEY = [
  { num: '01', when: 'Day 0', name: 'Apply', d: 'Submit the Chapter Lead application via /chapters/apply. Reviewed within 10 working days.' },
  { num: '02', when: 'Days 1–10', name: 'Shortlist', d: 'Personal review by Dr. Neha Dewan + Dr. Ross. Shortlisted candidates invited to a 30-min conversation.' },
  { num: '03', when: 'Days 10–21', name: 'Offer + Sign', d: 'Appointment Letter and MSA issued, reviewed by both legal teams, signed. Brand-licensing rights activate.' },
  { num: '04', when: 'Weeks 4–8', name: 'Onboarding', d: '90-day plan co-built with HQ. Dashboard access, brand toolkit, sourcing playbook, ministry intro packet.' },
  { num: '05', when: 'Ongoing', name: 'Operate', d: 'Source, convene, represent, govern, report. Weekly pipeline · Monthly report · Quarterly accountability.' },
  { num: '06', when: 'Quarter 4', name: 'Renew', d: 'Annual review against KPIs and territory P&L. Auto-renew for 12 months on satisfactory performance.' },
];

export default function ChaptersPage() {
  useSEO({
    title: 'SKI Axis Chapter Lead Programme · 11 Cities · Governance-First Accelerator',
    description: 'Become a SKI Axis Chapter Lead in your city. Source founders for the UAE governance-first accelerator. 20% revenue share + 1% equity per founder placed. London · Amsterdam · Mumbai · Bangalore · Riyadh · Singapore · Nairobi · Lagos · and more.',
  });

  return (
    <div className="cl-root" data-testid="chapters-page">
      <style>{STYLES}</style>
      <Header />

      <div className="cl-shell">
        {/* HERO */}
        <div className="cl-hero">
          <div className="cl-eb">SKI Axis Chapter Lead Programme · Global Expansion</div>
          <h1 className="cl-h1">Run a SKI Axis chapter <em>in your city</em>.</h1>
          <p className="cl-sub">
            We are appointing local ecosystem leaders in 11 territories to source founders into our governance-first
            accelerator and build the SKI Axis community in their market. Revenue share. Equity. Brand.
          </p>
          <div className="cl-cta-row">
            <Link to="/chapters/apply" className="cl-cta" data-testid="chapters-hero-apply">
              Apply to lead a chapter <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#territories" className="cl-cta-2" data-testid="chapters-hero-territories">
              See open territories
            </a>
          </div>
        </div>

        {/* WHAT IS IT */}
        <div className="cl-what" data-testid="chapters-what">
          <div className="cl-what-eb">The Model</div>
          <h2 className="cl-what-h">Distributed expansion. <em>Local accountability.</em> Institutional standards.</h2>
          <p className="cl-what-p">
            SKI Axis Chapter Leads are local ecosystem leaders who recruit and screen founders for their city's
            cohort, run governance clinics and pre-accelerator workshops on the ground, and represent SKI Axis at
            local events. Founders enter the same central AI-scored evaluation as everyone else — the Chapter Lead's
            job is to source quality applications, not to run the curriculum.
          </p>
          <p className="cl-what-p">
            The model rewards skin in the game. Chapter Leads share in programme revenue and take equity in the
            founders they place — so their reputation, their compensation, and their long-term upside all depend on
            the quality of the cohort they build.
          </p>
        </div>

        {/* THREE-TIER */}
        <div className="cl-tier">
          <h2 className="cl-h2">The three-tier <em>structure</em></h2>
          <p className="cl-h2-sub">SKI Axis runs as a hub-and-spoke network with clear separation between central governance, local sourcing, and domain expertise.</p>
          <div className="cl-tier-grid">
            <div className="cl-tier-card feat" data-testid="tier-hq">
              <div className="cl-tier-num">i. Tier One</div>
              <h3 className="cl-tier-name">SKI Axis <em>HQ</em> · Dubai</h3>
              <p className="cl-tier-d">Dr. Neha Dewan + Dr. Ross Rafahël. Central governance framework, AI-scored evaluation, the 12-week curriculum, investor relations, regulatory liaison with IFZA / DIFC / DMCC.</p>
              <div className="cl-tier-tag">Governance · AI · Capital</div>
            </div>
            <div className="cl-tier-card" data-testid="tier-chapters">
              <div className="cl-tier-num">ii. Tier Two</div>
              <h3 className="cl-tier-name">Chapter Leads · 11 cities</h3>
              <p className="cl-tier-d">Local ecosystem leaders. Founder sourcing, on-the-ground workshops, community building, brand presence at local events. Skin-in-the-game economic alignment.</p>
              <div className="cl-tier-tag">Local · Distributed</div>
            </div>
            <div className="cl-tier-card" data-testid="tier-mentors">
              <div className="cl-tier-num">iii. Tier Three</div>
              <h3 className="cl-tier-name">Mentors &amp; Advisors</h3>
              <p className="cl-tier-d">Domain experts (Pranav Jyoti, Dr. Amanulla Khan, Sameer Sortur, et al.) who provide investor introductions, governance advisory, sector expertise across cohorts.</p>
              <div className="cl-tier-tag">Expertise · Network</div>
            </div>
          </div>
        </div>

        {/* TERRITORIES */}
        <div className="cl-terr" id="territories">
          <h2 className="cl-h2">Open <em>territories</em></h2>
          <p className="cl-h2-sub">All territories below are currently accepting Chapter Lead applications. We appoint one Chapter Lead per city. Don't see your city? Propose it under "Other".</p>
          <div className="cl-terr-grid" data-testid="chapters-territories-grid">
            {TERRITORIES.map((t) => (
              <div key={t.name} className={`cl-terr-card ${t.hq ? 'hq' : ''}`} data-testid={`territory-${t.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="cl-terr-icn"><MapPin className="h-4 w-4" /></div>
                <div>
                  <div className="cl-terr-name">{t.name}</div>
                  <div className="cl-terr-status">{t.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* TERMS BAND */}
        <div className="cl-terms" data-testid="chapters-terms-band">
          <div className="cl-terms-block">
            <div className="cl-terms-eb">Revenue Share</div>
            <p className="cl-terms-num"><em>%</em> 20</p>
            <div className="cl-terms-d">Of every cohort fee from a founder you source — paid on cohort completion.</div>
          </div>
          <div className="cl-terms-block">
            <div className="cl-terms-eb">Equity Allocation</div>
            <p className="cl-terms-num"><em>%</em> 1.0</p>
            <div className="cl-terms-d">Common-stock advisor allocation per founder placed · 24-month vest from cohort completion.</div>
          </div>
          <div className="cl-terms-block">
            <div className="cl-terms-eb">Term</div>
            <p className="cl-terms-num">12 mo</p>
            <div className="cl-terms-d">Auto-renewing · 60-day notice · quarterly performance review.</div>
          </div>
        </div>

        {/* MUTUAL */}
        <div className="cl-mutual">
          <div className="cl-mutual-col" data-testid="chapters-what-you-get">
            <div className="cl-mutual-eb">What you receive</div>
            <h3 className="cl-mutual-h">As a <em>Chapter Lead</em>.</h3>
            <ul>
              <li><Award className="h-4 w-4" /><span><strong>20% revenue share</strong> on every founder you source who completes the cohort</span></li>
              <li><Sparkles className="h-4 w-4" /><span><strong>1% equity allocation</strong> per cohort company you place (24-month vest)</span></li>
              <li><Globe2 className="h-4 w-4" /><span>Named SKI Axis Chapter Lead — <strong>dedicated profile page</strong> on skiaxis.com</span></li>
              <li><Users className="h-4 w-4" /><span>Full access to the SKI Axis investor network, mentor pool, and governance frameworks</span></li>
              <li><Shield className="h-4 w-4" /><span>Brand-licensing rights for "SKI Axis · [Your City]" workshops and community events</span></li>
              <li><TrendingUp className="h-4 w-4" /><span><strong>Speaking seat</strong> at Invest UAE Forum future editions</span></li>
            </ul>
          </div>
          <div className="cl-mutual-col dark" data-testid="chapters-what-we-get">
            <div className="cl-mutual-eb">What SKI Axis receives</div>
            <h3 className="cl-mutual-h">From <em>you</em>.</h3>
            <ul>
              <li><Award className="h-4 w-4" /><span><strong>Qualified founder pipeline</strong> from your geography (target: 3–8 quality applicants per cohort)</span></li>
              <li><Sparkles className="h-4 w-4" /><span>Local market intelligence and ecosystem relationships</span></li>
              <li><Globe2 className="h-4 w-4" /><span>Brand presence in your city <strong>without us needing a physical office</strong></span></li>
              <li><Users className="h-4 w-4" /><span>Governance clinics + pre-accelerator workshops on the ground (4–6/year)</span></li>
              <li><Shield className="h-4 w-4" /><span>Quarterly accountability against agreed pipeline and conversion metrics</span></li>
              <li><TrendingUp className="h-4 w-4" /><span>SKI Axis representation at local investment, accelerator, and policy events</span></li>
            </ul>
          </div>
        </div>

        {/* FIVE PILLARS — ROLES & RESPONSIBILITIES */}
        <div className="cl-pillars" data-testid="chapters-pillars">
          <h2 className="cl-h2">The five <em>pillars</em> of the role</h2>
          <p className="cl-h2-sub">Every Chapter Lead carries the same five duties. The targets scale with the territory; the standard does not.</p>
          <div className="cl-pillar-grid">
            {PILLARS.map((p) => (
              <div key={p.name} className="cl-pillar" data-testid={`pillar-${p.name.toLowerCase()}`}>
                <div className="cl-pillar-icn">{p.icn}</div>
                <div className="cl-pillar-num">{p.num}</div>
                <h3 className="cl-pillar-name">{p.name}</h3>
                <p className="cl-pillar-d">{p.d}</p>
                <ul>{p.items.map((it, i) => <li key={i}>{it}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>

        {/* COMPENSATION ARCHITECTURE */}
        <div className="cl-comp" data-testid="chapters-compensation">
          <div className="cl-what-eb">Compensation Architecture</div>
          <h2 className="cl-comp-h">Five income streams. <em>One mandate.</em></h2>
          <p className="cl-comp-sub">
            Chapter Leads earn from primary cohort placements and four secondary streams. The framework rewards
            consistent quality over volume — and aligns your long-term upside with the founders you place.
          </p>

          <div className="cl-comp-grid">
            <div className="cl-comp-card" data-testid="comp-primary">
              <div className="cl-comp-eb">01 · Primary · Cohort Revenue Share</div>
              <p className="cl-comp-num"><em>%</em> 20</p>
              <p className="cl-comp-tag">Of every gross cohort fee from a founder you source. Paid within 30 days of cohort completion (Week 13). Pro-rata on instalments.</p>
            </div>
            <div className="cl-comp-card" data-testid="comp-equity">
              <div className="cl-comp-eb">02 · Long-term · Equity Allocation</div>
              <p className="cl-comp-num"><em>%</em> 1.0</p>
              <p className="cl-comp-tag">Common-stock advisor-style allocation per founder placed. Vesting 24 months from cohort completion. Standard accelerator grant terms.</p>
            </div>
            <div className="cl-comp-card" data-testid="comp-faculty">
              <div className="cl-comp-eb">03 · Recurring · Master Faculty</div>
              <p className="cl-comp-num"><em>%</em> 15</p>
              <p className="cl-comp-tag">Revenue share on Master Faculty webinar fees from anyone in your territory who registers within 90 days of placement.</p>
            </div>
            <div className="cl-comp-card" data-testid="comp-investors">
              <div className="cl-comp-eb">04 · Recurring · Investors Circle</div>
              <p className="cl-comp-num"><em>%</em> 10</p>
              <p className="cl-comp-tag">Origination fee on every Investors Circle membership you source in your city — paid on first annual renewal.</p>
            </div>
            <div className="cl-comp-card" data-testid="comp-carry">
              <div className="cl-comp-eb">05 · Long-term · Carried Interest</div>
              <p className="cl-comp-num"><em>%</em> 0.5</p>
              <p className="cl-comp-tag">Of SKI Axis's own carry on follow-on investments into a founder you placed. 4-year vest. Pure upside — no downside.</p>
            </div>
            <div className="cl-comp-card" data-testid="comp-honorarium">
              <div className="cl-comp-eb">06 · Recognition · Speaking Honorarium</div>
              <p className="cl-comp-num">USD <em>$</em>500–2,000</p>
              <p className="cl-comp-tag">Per Forum or Master Faculty stage you anchor — paid in addition to all of the above. Rates scale with audience and format.</p>
            </div>
          </div>

          <div className="cl-comp-example" data-testid="comp-example">
            <div className="cl-comp-example-eb">Worked Example · A typical Year-One Chapter Lead</div>
            <p>You place <strong>3 founders</strong> from your corridor into Cohort 2 at the institutional cohort fee of <span className="num">USD $12,000</span> each (gross).</p>
            <p>→ <strong>Year-1 cash:</strong> 3 × $12,000 × 20% = <span className="num">USD $7,200</span> in revenue share, paid Week 13.</p>
            <p>→ <strong>Year-1 equity:</strong> 3 × 1.0% advisor allocation, vesting over 24 months from cohort completion.</p>
            <p>→ Two of those founders bring 4 of their team-mates onto Master Faculty webinars at AED 500 each = <span className="num">USD $245</span> recurring revenue share.</p>
            <p>→ You source 2 Investors Circle memberships at USD $4,800/year each → 10% origination = <span className="num">USD $960</span>.</p>
            <p style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid rgba(200,168,75,.3)', fontSize: '15px' }}>
              <strong>Year-1 cash compensation:</strong> ~ <span className="num">USD $8,400</span> · <strong>Plus</strong> 3% advisor equity in 3 SKI Axis-graduated companies · <strong>Plus</strong> a permanent Founding Chapter Lead position on the platform.
            </p>
          </div>
        </div>

        {/* KPI / KRA TABLE */}
        <div className="cl-kpi" data-testid="chapters-kpi">
          <h2 className="cl-h2">Quarterly <em>accountability</em> · KPIs &amp; KRAs</h2>
          <p className="cl-h2-sub">Every Chapter Lead is reviewed quarterly against the same seven metrics. We weight pipeline quality and cohort placement most heavily — because that is what compounds.</p>
          <div className="cl-kpi-table">
            <div className="cl-kpi-row">
              <div className="cl-kpi-cell">Metric · KRA</div>
              <div className="cl-kpi-cell">Annual Target</div>
              <div className="cl-kpi-cell">Cadence</div>
              <div className="cl-kpi-cell">Weight</div>
            </div>
            {KPIS.map((k, i) => (
              <div key={i} className="cl-kpi-row" data-testid={`kpi-row-${i}`}>
                <div className="cl-kpi-cell metric">{k.metric}</div>
                <div className="cl-kpi-cell target">{k.target}</div>
                <div className="cl-kpi-cell cad">{k.cadence}</div>
                <div className="cl-kpi-cell weight">{k.weight}</div>
              </div>
            ))}
          </div>
        </div>

        {/* JOURNEY — END-TO-END LIFECYCLE */}
        <div className="cl-journey" data-testid="chapters-journey">
          <h2 className="cl-h2">The Chapter Lead <em>journey</em></h2>
          <p className="cl-h2-sub">Six stages from application to renewal — typical timeline 21 days from apply to signed.</p>
          <div className="cl-journey-rail">
            {JOURNEY.map((s, i) => (
              <div key={i} className="cl-stage" data-testid={`journey-stage-${i + 1}`}>
                <div className="cl-stage-num">{s.num}</div>
                <div className="cl-stage-when">{s.when}</div>
                <h3 className="cl-stage-name">{s.name}</h3>
                <p className="cl-stage-d">{s.d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="cl-faq">
          <h2 className="cl-h2">Frequently <em>asked</em></h2>
          <div className="cl-faq-grid" data-testid="chapters-faq">
            {FAQ.map((f, i) => (
              <div key={i} className="cl-faq-item" data-testid={`chapter-faq-${i}`}>
                <h4 className="cl-faq-q">{f.q}</h4>
                <p className="cl-faq-a">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FINAL CTA */}
        <div className="cl-final" data-testid="chapters-final-cta">
          <h3>Build the SKI Axis chapter <em>in your city</em>.</h3>
          <p>One Chapter Lead per territory. Applications reviewed on a rolling basis. London, Amsterdam, and Mumbai are advancing fast — apply this week if your city is on the list.</p>
          <Link to="/chapters/apply" className="cl-final-btn" data-testid="chapters-final-apply">
            Apply now <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
